import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface ClinicUser {
  id: string;
  email: string;
  name: string;
  role: string;
  clinicId: string;
  clinicName: string;
}

interface ClinicAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: ClinicUser | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const ClinicAuthContext = createContext<ClinicAuthContextType | undefined>(undefined);

const STORAGE_KEY_TOKEN = 'clinic_auth_token';
const STORAGE_KEY_USER = 'clinic_auth_user';

export function ClinicAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<ClinicUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const savedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
      const savedUser = localStorage.getItem(STORAGE_KEY_USER);
      if (savedToken && savedUser) {
        // Check if token is expired (decode JWT payload)
        const payload = JSON.parse(atob(savedToken.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp > now) {
          setToken(savedToken);
          setUser(JSON.parse(savedUser));
        } else {
          // Token expired, clear storage
          localStorage.removeItem(STORAGE_KEY_TOKEN);
          localStorage.removeItem(STORAGE_KEY_USER);
        }
      }
    } catch {
      // Invalid stored data, clear it
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      localStorage.removeItem(STORAGE_KEY_USER);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${apiUrl}/clinic-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Invalid email or password');
      }

      const data = await response.json();

      setToken(data.token);
      setUser(data.user);
      localStorage.setItem(STORAGE_KEY_TOKEN, data.token);
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(data.user));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setError(null);
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_USER);
  }, []);

  return (
    <ClinicAuthContext.Provider
      value={{
        isAuthenticated: !!token,
        isLoading,
        user,
        token,
        login,
        logout,
        error,
      }}
    >
      {children}
    </ClinicAuthContext.Provider>
  );
}

export function useClinicAuth() {
  const context = useContext(ClinicAuthContext);
  if (context === undefined) {
    throw new Error('useClinicAuth must be used within a ClinicAuthProvider');
  }
  return context;
}

/**
 * Drop-in replacement for ClinicAuthProvider used in the App Proxy build.
 *
 * No JWT — auth is transparent via Shopify's logged_in_customer_id.
 * Fetches the clinic profile once on mount to populate `user.clinicId`
 * and `user.clinicName` so existing pages work unchanged.
 */
export function ProxyClinicAuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<{ id: string; name: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Dynamic import avoids circular dep at module load time
    import('../services/clinicPortal').then(({ clinicPortalApi }) => {
      clinicPortalApi
        .getProfile()
        .then((p) => {
          setProfile({ id: p.id, name: p.name });
          setIsLoading(false);
        })
        .catch(() => {
          // api.ts 401 handler redirects to Shopify account login
          setIsLoading(false);
        });
    });
  }, []);

  const user: ClinicUser | null = profile
    ? {
        id: '',
        email: '',
        name: profile.name,
        role: 'clinic',
        clinicId: profile.id,
        clinicName: profile.name,
      }
    : null;

  // Render nothing while loading — prevents pages from rendering before clinicId is available
  if (isLoading) return null;

  return (
    <ClinicAuthContext.Provider
      value={{
        isAuthenticated: true, // Shopify handles auth via proxy params
        isLoading: false,
        user,
        token: null,
        login: async () => {},
        logout: () => {},
        error: null,
      }}
    >
      {children}
    </ClinicAuthContext.Provider>
  );
}
